import React, { useMemo, useRef, useState } from "react";
import { Form } from "react-router-dom";
import { toast } from "react-toastify";
import AddStagePlayDayMutation from "./mutation/AddStagePlayDay";
import { GlobalStyles } from "../../constants/styles";
import useThemeColors from "../organization/portal/useThemeColors";
import { alphaFromHex } from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../util/graphqlError";

export default function AddStagePlayDayModal({
                                                 stageId,
                                                 playDates,
                                                 setPlayDates,
                                                 modalElem,
                                             }) {
    const [addStagePlayDayMutation] = AddStagePlayDayMutation();
    const dayInput = useRef(null);
    const timeInput = useRef(null);
    const [submitting, setSubmitting] = useState(false);
    const colors = useThemeColors();
    const { t } = useTranslation();

    const ui = useMemo(() => {
        return {
            content: {
                borderRadius: 18,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.10)",
                boxShadow: "0 22px 60px rgba(0,0,0,0.18)",
            },
            header: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "12px 14px",
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(colors.primary, 0.50)},
                    ${alphaFromHex(colors.primary, 0.00)}
                )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
            },
            titleWrap: {
                display: "flex",
                alignItems: "center",
                gap: 10,
                minWidth: 0,
            },
            badge: {
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.03em",
                padding: "6px 10px",
                borderRadius: 999,
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: colors.textOnPrimary,
                whiteSpace: "nowrap",
            },
            title: {
                margin: 0,
                fontSize: 14,
                fontWeight: 900,
                color: "#14151a",
            },
            sub: {
                margin: 0,
                fontSize: 12.5,
                fontWeight: 700,
                color: colors.textOnPrimary,
            },
            close: {
                width: 38,
                height: 38,
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.10)",
                background: colors.primary,
                color: colors.textOnPrimary,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
            },
            body: { padding: 14 },
            card: {
                borderRadius: 14,
                padding: 12,
                border: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.015)",
            },
            label: {
                fontSize: 12.5,
                fontWeight: 900,
                color: "rgba(20,21,26,0.72)",
                marginBottom: 6,
            },
            footer: {
                display: "flex",
                justifyContent: "space-between",
                gap: 10,
                padding: "12px 14px",
                borderTop: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.01)",
            },
            btn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                border: "1px solid rgba(0,0,0,0.12)",
                background: "#fff",
                color: "#14151a",
                cursor: "pointer",
                minWidth: 120,
            },
            btnPrimary: {
                background: GlobalStyles.colors.canary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: GlobalStyles.colors.woodsmoke,
            },
            helper: {
                marginTop: 10,
                fontSize: 12.5,
                fontWeight: 700,
                color: "rgba(20,21,26,0.55)",
            },
        };
    }, [colors.primary, colors.textOnPrimary]);

    const DAYS = useMemo(
        () => [
            { id: 0, name: t("addStagePlayDayModal.days.monday") },
            { id: 1, name: t("addStagePlayDayModal.days.tuesday") },
            { id: 2, name: t("addStagePlayDayModal.days.wednesday") },
            { id: 3, name: t("addStagePlayDayModal.days.thursday") },
            { id: 4, name: t("addStagePlayDayModal.days.friday") },
            { id: 5, name: t("addStagePlayDayModal.days.saturday") },
            { id: 6, name: t("addStagePlayDayModal.days.sunday") },
        ],
        [t]
    );

    const refreshPlayDays = (result) => {
        const date = result?.data?.createStagePlayDay?.stagePlayDay;
        if (!date) return;

        if (modalElem?.current?.click) {
            modalElem.current.click();
        }

        setPlayDates([
            ...(playDates || []),
            {
                id: date.id,
                day: date.day,
                fromTime: date.fromTime,
            },
        ]);

        if (dayInput.current) dayInput.current.value = "";
        if (timeInput.current) timeInput.current.value = "";

        toast.success(t("addStagePlayDayModal.toasts.addedSuccessfully"), {
            autoClose: 3000,
            toastId: "stage_play_day_added",
            position: "top-center",
            closeOnClick: true,
        });
    };

    const errorHandler = (error) => {
        toast.error(
            getLocalizedGraphQLError(error, t, "addStagePlayDayModal.toasts.somethingWentWrong"),
            {
                autoClose: 3000,
                toastId: "stage_play_day_error",
                position: "top-center",
                closeOnClick: true,
            }
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;

        const dayRaw = dayInput.current?.value ?? "";
        const fromTime = timeInput.current?.value ?? "";

        if (!stageId) {
            toast.error(t("addStagePlayDayModal.toasts.stageRequired"), {
                autoClose: 3000,
                toastId: "stage_play_day_stage_required",
                position: "top-center",
                closeOnClick: true,
            });
            return;
        }

        if (!dayRaw) {
            toast.error(t("addStagePlayDayModal.toasts.dayRequired"), {
                autoClose: 3000,
                toastId: "stage_play_day_day_required",
                position: "top-center",
                closeOnClick: true,
            });
            return;
        }

        if (!fromTime) {
            toast.error(t("addStagePlayDayModal.toasts.timeRequired"), {
                autoClose: 3000,
                toastId: "stage_play_day_time_required",
                position: "top-center",
                closeOnClick: true,
            });
            return;
        }

        const day = parseInt(dayRaw, 10);
        if (Number.isNaN(day)) {
            toast.error(t("addStagePlayDayModal.toasts.invalidDay"), {
                autoClose: 3000,
                toastId: "stage_play_day_day_invalid",
                position: "top-center",
                closeOnClick: true,
            });
            return;
        }

        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        setSubmitting(true);
        try {
            const result = await addStagePlayDayMutation({
                variables: {
                    stageId: parseInt(stageId, 10),
                    day,
                    fromTime,
                    timezone,
                },
            });
            refreshPlayDays(result);
        } catch (err) {
            errorHandler(err);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <div className="modal fade" id="add-playday-modal">
                <div className="modal-dialog modal-lg">
                    <div className="modal-content" style={ui.content}>
                        <Form method="post" onSubmit={handleSubmit}>
                            <div className="modal-header" style={ui.header}>
                                <div style={ui.titleWrap}>
                                    <span style={ui.badge}>
                                        <i className="fas fa-calendar-plus" />
                                    </span>

                                    <div style={{ minWidth: 0 }}>
                                        <h4 className="modal-title" style={ui.title}>
                                            {t("addStagePlayDayModal.header.title")}
                                        </h4>
                                        <p style={ui.sub}>
                                            {t("addStagePlayDayModal.header.subtitle")}
                                        </p>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    style={ui.close}
                                    data-dismiss="modal"
                                    aria-label={t("addStagePlayDayModal.header.close")}
                                    title={t("addStagePlayDayModal.header.close")}
                                    disabled={submitting}
                                >
                                    <span
                                        aria-hidden="true"
                                        style={{ fontSize: 18, fontWeight: 900 }}
                                    >
                                        &times;
                                    </span>
                                </button>
                            </div>

                            <div className="modal-body" style={ui.body}>
                                <div style={ui.card}>
                                    <div className="row">
                                        <div className="col-sm-6">
                                            <div className="form-group">
                                                <div style={ui.label}>
                                                    {t("addStagePlayDayModal.fields.day")}
                                                </div>
                                                <select
                                                    className="form-control select2bs4"
                                                    name="day"
                                                    required
                                                    ref={dayInput}
                                                    defaultValue=""
                                                    disabled={submitting}
                                                >
                                                    <option value="" disabled>
                                                        {t("addStagePlayDayModal.fields.chooseDay")}
                                                    </option>
                                                    {DAYS.map((day) => (
                                                        <option key={day.id} value={day.id}>
                                                            {day.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        <div className="col-sm-6">
                                            <div className="form-group">
                                                <div style={ui.label}>
                                                    {t("addStagePlayDayModal.fields.time")}
                                                </div>
                                                <input
                                                    type="time"
                                                    className="form-control"
                                                    required
                                                    id="add-playday-timeInput"
                                                    name="time"
                                                    step="1"
                                                    placeholder={t("addStagePlayDayModal.fields.enterTime")}
                                                    ref={timeInput}
                                                    disabled={submitting}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div style={ui.helper}>
                                        {t("addStagePlayDayModal.helper.tip")}
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer" style={ui.footer}>
                                <button
                                    type="button"
                                    style={ui.btn}
                                    data-dismiss="modal"
                                    disabled={submitting}
                                >
                                    {t("addStagePlayDayModal.buttons.close")}
                                </button>

                                <button
                                    type="submit"
                                    style={{ ...ui.btn, ...ui.btnPrimary }}
                                    disabled={submitting}
                                >
                                    {submitting ? (
                                        <>
                                            <span
                                                className="spinner-border spinner-border-sm"
                                                role="status"
                                                aria-hidden="true"
                                            />
                                            {t("addStagePlayDayModal.buttons.saving")}
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-check" />
                                            {t("addStagePlayDayModal.buttons.save")}
                                        </>
                                    )}
                                </button>
                            </div>
                        </Form>
                    </div>
                </div>
            </div>

            <style>{`
                #add-playday-modal .form-control {
                  border-radius: 12px;
                  border: 1px solid rgba(0,0,0,0.12);
                  font-weight: 800;
                }

                #add-playday-modal .form-control:focus {
                  outline: none;
                  box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
                  border-color: rgba(0,0,0,0.12);
                }

                #add-playday-modal button:focus {
                  outline: none;
                  box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
                }
            `}</style>
        </>
    );
}
